import { KatSpinner, KatStatusindicator } from '@amzn/katal-react';
import React, { FC } from 'react';
import styles from './StatusIndicator.module.css';

export interface StatusIndicatorProps {
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Whether the operation is loading
   */
  isLoading?: boolean;
  /**
   * Whether the operation was successful
   */
  isSuccess?: boolean;
  /**
   * Label for the success state
   * @default "Success"
   */
  successLabel?: string;
  /**
   * Additional class name for the container
   */
  className?: string;
  /**
   * Test ID for the component
   * @default "status-indicator"
   */
  'data-testid'?: string;
}

/**
 * A reusable status indicator component that shows loading, success, and error states
 *
 * @example
 * ```tsx
 * <StatusIndicator
 *   isLoading={isSubmitting}
 *   isSuccess={isSubmitted}
 *   error={submitError}
 *   successLabel="Saved successfully"
 * />
 * ```
 */
export const StatusIndicator: FC<StatusIndicatorProps> = ({
  error,
  isLoading,
  isSuccess,
  successLabel = 'Success',
  className,
  'data-testid': testId = 'status-indicator',
}) => (
  <div className={`${styles.container} ${className || ''}`} data-testid={testId}>
    {error ? (
      <KatStatusindicator label={error} variant="error" data-testid={`${testId}-error`} />
    ) : isSuccess ? (
      <KatStatusindicator
        label={successLabel}
        variant="success"
        data-testid={`${testId}-success`}
      />
    ) : isLoading ? (
      <KatSpinner size="small" data-testid={`${testId}-loading`} />
    ) : null}
  </div>
);
